/**
 * Cuestionario interactivo del Call Sidebar.
 *
 * Se rellena DURANTE la llamada. Diseñado para ser rápido:
 * chips tap-to-select, booleans Sí/No, textareas mínimas.
 *
 * Secciones:
 * 1. Tipo de llamada (chips)
 * 2. Datos primera llamada / intake (colapsable)
 * 3. Servicio propuesto (chips)
 * 4. Grading (chips color-coded)
 * 5. Notas rápidas (textarea)
 */

import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { cn } from '@/lib/utils';
import { ChevronDownIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import type { QuestionnaireData, CallTypeCode, GradingValue } from '@/types/callSidebar';

// ─── Constants ───────────────────────────────────────────────────────────────

const CALL_TYPES: { code: CallTypeCode; label: string; emoji: string }[] = [
  { code: 'primera_llamada', label: 'Primera', emoji: '1️⃣' },
  { code: 'seguimiento', label: 'Seguimiento', emoji: '🔄' },
  { code: 'venta', label: 'Venta', emoji: '💰' },
];

const SERVICIOS = [
  'Residencia',
  'Nacionalidad',
  'Arraigo Social',
  'Arraigo Laboral',
  'Arraigo Familiar',
  'Reagrupación',
  'Asilo',
  'Modificación',
  'Otro',
];

const GRADINGS: { value: GradingValue; label: string; color: string }[] = [
  { value: 'A', label: 'A', color: 'bg-green-100 text-green-800 border-green-300 hover:bg-green-200' },
  { value: 'B+', label: 'B+', color: 'bg-blue-100 text-blue-800 border-blue-300 hover:bg-blue-200' },
  { value: 'B-', label: 'B-', color: 'bg-yellow-100 text-yellow-800 border-yellow-300 hover:bg-yellow-200' },
  { value: 'C', label: 'C', color: 'bg-orange-100 text-orange-800 border-orange-300 hover:bg-orange-200' },
  { value: 'D', label: 'D', color: 'bg-red-100 text-red-800 border-red-300 hover:bg-red-200' },
];

const ESTADOS_CIVILES = ['Soltero/a', 'Casado/a', 'Divorciado/a', 'Viudo/a', 'Pareja de hecho', 'Separado/a'];

const NIVELES_ESTUDIOS = ['Sin estudios', 'Primaria', 'Secundaria/ESO', 'Bachillerato', 'FP/Grado medio', 'FP/Grado superior', 'Universidad', 'Máster/Doctorado'];

const DOCUMENTOS_POSIBLES = ['Pasaporte', 'NIE', 'Empadronamiento', 'Contrato trabajo', 'Vida laboral', 'Antecedentes penales', 'Certificado nacimiento', 'Certificado matrimonio'];

const URGENCIAS: { value: 'alta' | 'media' | 'baja'; label: string; color: string }[] = [
  { value: 'alta', label: 'Alta', color: 'bg-red-100 text-red-800 border-red-300 hover:bg-red-200' },
  { value: 'media', label: 'Media', color: 'bg-yellow-100 text-yellow-800 border-yellow-300 hover:bg-yellow-200' },
  { value: 'baja', label: 'Baja', color: 'bg-green-100 text-green-800 border-green-300 hover:bg-green-200' },
];

const PROVINCIAS = [
  'Álava', 'Albacete', 'Alicante', 'Almería', 'Asturias', 'Ávila',
  'Badajoz', 'Barcelona', 'Burgos', 'Cáceres', 'Cádiz', 'Cantabria',
  'Castellón', 'Ciudad Real', 'Córdoba', 'A Coruña', 'Cuenca',
  'Gerona', 'Granada', 'Guadalajara', 'Guipúzcoa', 'Huelva', 'Huesca',
  'Islas Baleares', 'Jaén', 'León', 'Lérida', 'Lugo', 'Madrid',
  'Málaga', 'Murcia', 'Navarra', 'Ourense', 'Palencia', 'Las Palmas',
  'Pontevedra', 'La Rioja', 'Salamanca', 'Sta. Cruz de Tenerife',
  'Segovia', 'Sevilla', 'Soria', 'Tarragona', 'Teruel', 'Toledo',
  'Valencia', 'Valladolid', 'Vizcaya', 'Zamora', 'Zaragoza',
];

// ─── Sub-components ──────────────────────────────────────────────────────────

function ChipSelect<T extends string>({
  options,
  value,
  onChange,
}: {
  options: { code: T; label: string; emoji?: string }[];
  value: T | '';
  onChange: (v: T) => void;
}) {
  return (
    <div className="flex flex-wrap gap-1.5">
      {options.map((opt) => (
        <button
          key={opt.code}
          type="button"
          onClick={() => onChange(opt.code)}
          className={cn(
            'inline-flex items-center gap-1 px-2.5 py-1 rounded-lg border text-xs font-medium transition-all',
            value === opt.code
              ? 'bg-blue-100 text-blue-800 border-blue-400 ring-1 ring-blue-400 scale-[1.02]'
              : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50',
          )}
        >
          {opt.emoji && <span>{opt.emoji}</span>}
          {opt.label}
        </button>
      ))}
    </div>
  );
}

function BooleanChips({
  value,
  onChange,
  yesLabel = 'Sí',
  noLabel = 'No',
}: {
  value: boolean | undefined;
  onChange: (v: boolean) => void;
  yesLabel?: string;
  noLabel?: string;
}) {
  return (
    <div className="flex gap-1.5">
      <button
        type="button"
        onClick={() => onChange(true)}
        className={cn(
          'px-3 py-1 rounded-lg border text-xs font-medium transition-all',
          value === true
            ? 'bg-green-100 text-green-800 border-green-400 ring-1 ring-green-400'
            : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50',
        )}
      >
        {yesLabel}
      </button>
      <button
        type="button"
        onClick={() => onChange(false)}
        className={cn(
          'px-3 py-1 rounded-lg border text-xs font-medium transition-all',
          value === false
            ? 'bg-red-100 text-red-700 border-red-300 ring-1 ring-red-400'
            : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50',
        )}
      >
        {noLabel}
      </button>
    </div>
  );
}

function GradingChips({
  value,
  onChange,
}: {
  value: GradingValue;
  onChange: (v: GradingValue) => void;
}) {
  return (
    <div className="flex gap-1">
      {GRADINGS.map((g) => (
        <button
          key={g.value}
          type="button"
          onClick={() => onChange(value === g.value ? '' : g.value)}
          className={cn(
            'w-9 h-8 rounded-lg border text-xs font-bold transition-all',
            value === g.value
              ? cn(g.color, 'ring-1 ring-offset-1 ring-blue-400 scale-105')
              : 'bg-white text-gray-500 border-gray-200 hover:bg-gray-50',
          )}
        >
          {g.label}
        </button>
      ))}
    </div>
  );
}

function CollapsibleSection({
  title,
  defaultOpen = false,
  badge,
  children,
}: {
  title: string;
  defaultOpen?: boolean;
  badge?: string;
  children: React.ReactNode;
}) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border-t border-gray-100 pt-2">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center gap-2 py-1 text-xs font-semibold text-gray-500 uppercase tracking-wide hover:text-gray-700 transition-colors"
      >
        {open ? (
          <ChevronDownIcon className="h-3 w-3" />
        ) : (
          <ChevronRightIcon className="h-3 w-3" />
        )}
        {title}
        {badge && (
          <span className="text-[10px] font-normal normal-case bg-blue-50 text-blue-600 px-1.5 rounded-full">
            {badge}
          </span>
        )}
      </button>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.15 }}
            className="overflow-hidden"
          >
            <div className="space-y-3 py-2">{children}</div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="space-y-1">
      <label className="text-[11px] font-medium text-gray-500">{label}</label>
      {children}
    </div>
  );
}

// ─── Props ───────────────────────────────────────────────────────────────────

interface CallSidebarQuestionnaireProps {
  data: QuestionnaireData;
  onChange: (patch: Partial<QuestionnaireData>) => void;
  isFirstCall: boolean;
  /** Llamada activa (habilitar edición) */
  disabled?: boolean;
}

// ─── Component ───────────────────────────────────────────────────────────────

export function CallSidebarQuestionnaire({
  data,
  onChange,
  isFirstCall,
  disabled = false,
}: CallSidebarQuestionnaireProps) {
  // Count filled intake fields
  const intakeFieldCount = [
    data.city, data.state, data.nacionalidad, data.fecha_llegada_espana,
    data.empadronado !== undefined ? 'x' : '', data.profesion,
  ].filter(Boolean).length;

  return (
    <div className={cn('px-4 py-3 space-y-3', disabled && 'opacity-60 pointer-events-none')}>
      {/* ── Tipo de llamada ── */}
      <Field label="Tipo de llamada">
        <ChipSelect
          options={CALL_TYPES}
          value={data.call_type}
          onChange={(v) => onChange({ call_type: v })}
        />
      </Field>

      {/* ── Datos primera llamada (intake) ── */}
      {isFirstCall && (
        <CollapsibleSection
          title="Datos del contacto"
          defaultOpen={data.call_type === 'primera_llamada'}
          badge={intakeFieldCount > 0 ? `${intakeFieldCount}/6` : undefined}
        >
          <Field label="Ciudad">
            <input
              type="text"
              value={data.city}
              onChange={(e) => onChange({ city: e.target.value })}
              placeholder="Ej: Madrid"
              className="w-full rounded-lg border border-gray-200 bg-gray-50 px-2.5 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-blue-400"
            />
          </Field>

          <Field label="Provincia">
            <select
              value={data.state}
              onChange={(e) => onChange({ state: e.target.value })}
              className="w-full rounded-lg border border-gray-200 bg-gray-50 px-2.5 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-blue-400"
            >
              <option value="">Seleccionar...</option>
              {PROVINCIAS.map((p) => (
                <option key={p} value={p}>{p}</option>
              ))}
            </select>
          </Field>

          <Field label="Nacionalidad">
            <input
              type="text"
              value={data.nacionalidad}
              onChange={(e) => onChange({ nacionalidad: e.target.value })}
              placeholder="Ej: Colombiana"
              className="w-full rounded-lg border border-gray-200 bg-gray-50 px-2.5 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-blue-400"
            />
          </Field>

          <Field label="Fecha de llegada a España">
            <input
              type="date"
              value={data.fecha_llegada_espana}
              onChange={(e) => onChange({ fecha_llegada_espana: e.target.value })}
              className="w-full rounded-lg border border-gray-200 bg-gray-50 px-2.5 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-blue-400"
            />
          </Field>

          <Field label="¿Empadronado/a?">
            <BooleanChips
              value={data.empadronado}
              onChange={(v) => onChange({ empadronado: v })}
            />
          </Field>

          <Field label="¿Familiares en España?">
            <BooleanChips
              value={data.tiene_familiares_espana}
              onChange={(v) => onChange({ tiene_familiares_espana: v })}
            />
            {data.tiene_familiares_espana && (
              <input
                type="text"
                value={data.familiares_espana_detalle}
                onChange={(e) => onChange({ familiares_espana_detalle: e.target.value })}
                placeholder="Detalle (parentesco, situación...)"
                className="mt-1 w-full rounded-lg border border-gray-200 bg-gray-50 px-2.5 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-blue-400"
              />
            )}
          </Field>

          <Field label="Profesión">
            <input
              type="text"
              value={data.profesion}
              onChange={(e) => onChange({ profesion: e.target.value })}
              placeholder="Ej: Albañil, Ingeniera..."
              className="w-full rounded-lg border border-gray-200 bg-gray-50 px-2.5 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-blue-400"
            />
          </Field>

          <Field label="¿Trabajando actualmente?">
            <BooleanChips
              value={data.trabajando_actualmente}
              onChange={(v) => onChange({ trabajando_actualmente: v })}
            />
            {data.trabajando_actualmente && (
              <input
                type="text"
                value={data.tipo_trabajo_detalle}
                onChange={(e) => onChange({ tipo_trabajo_detalle: e.target.value })}
                placeholder="Detalle (empresa, sector...)"
                className="mt-1 w-full rounded-lg border border-gray-200 bg-gray-50 px-2.5 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-blue-400"
              />
            )}
          </Field>
        </CollapsibleSection>
      )}

      {/* ── Perfil personal ampliado ── */}
      <CollapsibleSection title="Perfil personal" badge={data.estado_civil || data.nivel_estudios ? '\u2713' : undefined}>
        <Field label="Estado civil">
          <select
            value={data.estado_civil}
            onChange={(e) => onChange({ estado_civil: e.target.value })}
            className="w-full rounded-lg border border-gray-200 bg-gray-50 px-2.5 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-blue-400"
          >
            <option value="">Seleccionar...</option>
            {ESTADOS_CIVILES.map((ec) => (
              <option key={ec} value={ec}>{ec}</option>
            ))}
          </select>
        </Field>
        <Field label="Hijos menores de edad">
          <input
            type="number"
            min={0}
            max={20}
            value={data.hijos_menores ?? ''}
            onChange={(e) => onChange({ hijos_menores: e.target.value ? Number(e.target.value) : undefined })}
            placeholder="0"
            className="w-20 rounded-lg border border-gray-200 bg-gray-50 px-2.5 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-blue-400"
          />
        </Field>
        <Field label="Nivel de estudios">
          <select
            value={data.nivel_estudios}
            onChange={(e) => onChange({ nivel_estudios: e.target.value })}
            className="w-full rounded-lg border border-gray-200 bg-gray-50 px-2.5 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-blue-400"
          >
            <option value="">Seleccionar...</option>
            {NIVELES_ESTUDIOS.map((ne) => (
              <option key={ne} value={ne}>{ne}</option>
            ))}
          </select>
        </Field>
      </CollapsibleSection>

      {/* ── Documentacion ── */}
      <CollapsibleSection title="Documentacion" badge={data.tiene_documentacion !== undefined ? '\u2713' : undefined}>
        <Field label="Tiene documentacion?">
          <BooleanChips
            value={data.tiene_documentacion}
            onChange={(v) => onChange({ tiene_documentacion: v })}
          />
        </Field>
        {data.tiene_documentacion && (
          <Field label="Documentos disponibles">
            <div className="flex flex-wrap gap-1.5">
              {DOCUMENTOS_POSIBLES.map((doc) => {
                const selected = (data.documentos_disponibles || []).includes(doc);
                return (
                  <button
                    key={doc}
                    type="button"
                    onClick={() => {
                      const current = data.documentos_disponibles || [];
                      onChange({
                        documentos_disponibles: selected
                          ? current.filter((d: string) => d !== doc)
                          : [...current, doc],
                      });
                    }}
                    className={cn(
                      'px-2 py-1 rounded-lg border text-[11px] font-medium transition-all',
                      selected
                        ? 'bg-emerald-100 text-emerald-800 border-emerald-400 ring-1 ring-emerald-400'
                        : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50',
                    )}
                  >
                    {selected ? '\u2713 ' : ''}{doc}
                  </button>
                );
              })}
            </div>
          </Field>
        )}
      </CollapsibleSection>

      {/* ── Servicio propuesto ── */}
      <Field label="Servicio propuesto">
        <div className="flex flex-wrap gap-1.5">
          {SERVICIOS.map((svc) => (
            <button
              key={svc}
              type="button"
              onClick={() => onChange({
                servicio_propuesto: data.servicio_propuesto === svc ? '' : svc,
              })}
              className={cn(
                'px-2.5 py-1 rounded-lg border text-xs font-medium transition-all',
                data.servicio_propuesto === svc
                  ? 'bg-purple-100 text-purple-800 border-purple-400 ring-1 ring-purple-400'
                  : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50',
              )}
            >
              {svc}
            </button>
          ))}
        </div>
        {data.servicio_propuesto === 'Otro' && (
          <input
            type="text"
            value={data.servicio_detalle}
            onChange={(e) => onChange({ servicio_detalle: e.target.value })}
            placeholder="Especificar servicio..."
            className="mt-1.5 w-full rounded-lg border border-gray-200 bg-gray-50 px-2.5 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-blue-400"
          />
        )}
      </Field>

      {/* ── Urgencia del cliente ── */}
      <Field label="Urgencia del cliente">
        <div className="flex gap-1.5">
          {URGENCIAS.map((u) => (
            <button
              key={u.value}
              type="button"
              onClick={() => onChange({ urgencia_cliente: data.urgencia_cliente === u.value ? '' : u.value })}
              className={cn(
                'flex-1 px-2.5 py-1.5 rounded-lg border text-xs font-medium transition-all text-center',
                data.urgencia_cliente === u.value
                  ? cn(u.color, 'ring-1 ring-offset-1 ring-blue-400')
                  : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50',
              )}
            >
              {u.label}
            </button>
          ))}
        </div>
      </Field>

      {/* ── Grading ── */}
      <CollapsibleSection title="Evaluación" defaultOpen>
        <Field label="Grading llamada">
          <GradingChips
            value={data.grading_llamada}
            onChange={(v) => onChange({ grading_llamada: v })}
          />
        </Field>
        <Field label="Grading situacion">
          <GradingChips
            value={data.grading_situacion}
            onChange={(v) => onChange({ grading_situacion: v })}
          />
        </Field>
        <Field label="Probabilidad de conversion">
          <div className="flex items-center gap-3">
            <input
              type="range"
              min={0}
              max={100}
              step={5}
              value={data.probabilidad_conversion ?? 50}
              onChange={(e) => onChange({ probabilidad_conversion: Number(e.target.value) })}
              className="flex-1 h-1.5 accent-blue-500"
            />
            <span className={cn(
              'text-xs font-bold min-w-[2.5rem] text-center rounded-lg px-1.5 py-0.5',
              (data.probabilidad_conversion ?? 50) >= 70 ? 'bg-green-100 text-green-700' :
              (data.probabilidad_conversion ?? 50) >= 40 ? 'bg-yellow-100 text-yellow-700' :
              'bg-red-100 text-red-700',
            )}>
              {data.probabilidad_conversion ?? 50}%
            </span>
          </div>
        </Field>
      </CollapsibleSection>

      {/* ── Notas rápidas ── */}
      <Field label="Notas rápidas">
        <textarea
          value={data.notes}
          onChange={(e) => onChange({ notes: e.target.value })}
          placeholder="Apuntes durante la llamada..."
          rows={3}
          className="w-full rounded-lg border border-gray-200 bg-gray-50 px-2.5 py-1.5 text-xs resize-none focus:outline-none focus:ring-1 focus:ring-blue-400"
        />
      </Field>
    </div>
  );
}
